// src/theme/healthcareTheme.js - Material-UI theme for clinical trials platform
import { createTheme } from '@mui/material/styles';

export const healthcareTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1565C0', // Medical blue - trust and professionalism
      light: '#42A5F5',
      dark: '#0D47A1',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#00C853', // Health green - positive outcomes
      light: '#69F0AE', 
      dark: '#00A148',
      contrastText: '#ffffff'
    },
    error: {
      main: '#D32F2F', // Medical alert red
      light: '#EF5350',
      dark: '#C62828'
    },
    warning: {
      main: '#FF6F00', // Caution orange
      light: '#FF8F00',
      dark: '#E65100'
    },
    info: {
      main: '#0277BD', // Information blue
      light: '#03A9F4',
      dark: '#01579B'
    },
    success: {
      main: '#2E7D32', // Success green
      light: '#4CAF50',
      dark: '#1B5E20'
    },
    background: {
      default: '#F8F9FA', // Clean medical background
      paper: '#FFFFFF'
    },
    text: {
      primary: '#212121',
      secondary: '#757575'
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      color: '#1565C0',
      lineHeight: 1.2
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#1565C0',
      lineHeight: 1.3
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      color: '#424242',
      lineHeight: 1.4
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#424242',
      lineHeight: 1.4
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#424242',
      lineHeight: 1.5
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 500,
      color: '#424242',
      lineHeight: 1.5
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#212121'
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#757575'
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem'
    }
  },
  spacing: 8, // 8px base spacing unit
  shape: {
    borderRadius: 12 // Rounded corners for modern medical software look
  },
  components: {
    // Card styling for medical data presentation
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #E0E0E0',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
          }
        }
      }
    },
    
    // Button styling for healthcare actions
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          minHeight: '44px', // Accessibility compliance
          fontSize: '0.875rem'
        },
        containedPrimary: {
          backgroundColor: '#1565C0',
          '&:hover': {
            backgroundColor: '#0D47A1'
          }
        },
        containedSecondary: {
          backgroundColor: '#00C853',
          '&:hover': {
            backgroundColor: '#00A148'
          }
        }
      }
    },
    
    // Chip styling for medical tags and status indicators
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
          fontSize: '0.75rem'
        },
        colorPrimary: {
          backgroundColor: '#E3F2FD',
          color: '#1565C0'
        },
        colorSecondary: {
          backgroundColor: '#E8F5E8',
          color: '#2E7D32'
        }
      }
    },
    
    // Paper styling for sections and containers
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #E0E0E0'
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }
      }
    },
    
    // TextField styling for forms and inputs
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#E0E0E0'
            },
            '&:hover fieldset': {
              borderColor: '#1565C0'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1565C0',
              borderWidth: 2
            }
          }
        }
      }
    },
    
    // AppBar styling for navigation
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#212121',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          borderBottom: '1px solid #E0E0E0'
        }
      }
    },
    
    // Tab styling for navigation
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: '48px',
          color: '#757575',
          '&.Mui-selected': {
            color: '#1565C0',
            fontWeight: 600
          }
        }
      }
    },
    
    // DataGrid styling for clinical data tables
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: '1px solid #E0E0E0',
          borderRadius: 12,
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #F0F0F0'
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F8F9FA',
            color: '#424242',
            fontWeight: 600,
            borderBottom: '2px solid #E0E0E0'
          }
        }
      }
    },
    
    // Alert styling for medical notifications
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500
        },
        standardError: {
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          border: '1px solid #FFCDD2'
        },
        standardWarning: {
          backgroundColor: '#FFF8E1',
          color: '#E65100',
          border: '1px solid #FFECB3'
        },
        standardInfo: {
          backgroundColor: '#E3F2FD',
          color: '#1565C0',
          border: '1px solid #BBDEFB'
        },
        standardSuccess: {
          backgroundColor: '#E8F5E8',
          color: '#2E7D32',
          border: '1px solid #C8E6C9'
        }
      }
    }
  }
});

// Dark mode theme for night shifts or user preference
export const healthcareDarkTheme = createTheme({
  ...healthcareTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#42A5F5',
      light: '#90CAF9',
      dark: '#1565C0',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#69F0AE',
      light: '#B9F6CA',
      dark: '#00C853',
      contrastText: '#000000'
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E'
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0'
    }
  }
});

export default healthcareTheme;